// #563: Die Bereiche des Dashboards als Register statt fest verdrahteter
// Spalten. Das Dashboard rendert die sichtbaren Bereiche in der Reihenfolge,
// die sichtbareBereiche() liefert; was ein Bereich zeigt, steht weiterhin im
// Dashboard (Darstellung je id), weil es an dessen Zustand haengt und dieser
// das fruehe `return` bei Laden/Fehler ueberleben muss (Regel aus #471).
//
// #569: Jeder Bereich laesst sich in den Einstellungen ausblenden und
// sortieren (Schluessel `dashboard`: `ausgeblendet` und `reihenfolge`, je eine
// Liste von ids). Ausblenden ist eine Vorliebe, kein Recht: `bedingung`
// (Rolle) gilt zuerst. Ein Bereich, den die gespeicherte Reihenfolge nicht
// kennt (neu im Register, spaeter eigene Listen aus #552 als `typ: 'liste'`),
// steht hinten.
//
// "Heute" hat zwei Plaetze: als Terminliste (Band ueber dem Raster) oder als
// Tageskalender im Raster. Das entscheidet `showTodayCalendar`.
//
// #650: `standardAus` macht einen Bereich von sich aus unsichtbar. Er erscheint
// erst, wenn seine id in `eingeblendet` steht (Einstellungen), und taucht
// nicht in der Leiste „Ausgeblendete Bereiche“ auf: Die stünde sonst nach dem
// Update auf jedem Dashboard. `ausgeblendet` gilt für ihn trotzdem.

export const BEREICHE = [
  { id: 'heute', typ: 'fest', titelKey: 'dashboard.todayLabel', titel: 'Heute', platz: (z) => (z.showTodayCalendar ? 'raster' : 'band') },
  { id: 'meine', typ: 'fest', titelKey: 'dashboard.myTickets', titel: 'Meine' },
  // Den Pool sehen nur Manager (#44-Revision 2026-10-19)
  { id: 'pool', typ: 'fest', titelKey: 'dashboard.pool', titel: 'Pool', bedingung: (z) => z.siehtPool },
  { id: 'mails', typ: 'fest', titelKey: 'dashboard.emails', titel: 'Mails' },
  { id: 'teams', typ: 'fest', titelKey: 'dashboard.teams', titel: 'Teams' },
  // #650: versehentlich abgeschlossene Tickets wiederfinden
  { id: 'zuletzt', typ: 'fest', titelKey: 'dashboard.zuletzt', titel: 'Zuletzt abgeschlossen', standardAus: true }
];

const liste = (w) => (Array.isArray(w) ? w : []);

/**
 * Das Register in der gespeicherten Reihenfolge. Was die Liste nennt, steht
 * vorn in ihrer Folge; der Rest folgt in Register-Reihenfolge.
 */
export function ordneBereiche(register, reihenfolge) {
  const folge = liste(reihenfolge);
  const rang = (b) => { const i = folge.indexOf(b.id); return i === -1 ? Infinity : i; };
  return register
    .map((b, i) => ({ b, i }))
    .sort((x, y) => (rang(x.b) - rang(y.b)) || (x.i - y.i))
    .map(({ b }) => b);
}

/** Ist der Bereich ausgeblendet, ausdrücklich oder weil er von sich aus aus ist (#650)? */
export function istAusgeblendet(bereich, zustand) {
  if (liste(zustand.ausgeblendet).includes(bereich.id)) return true;
  return !!bereich.standardAus && !liste(zustand.eingeblendet).includes(bereich.id);
}

/**
 * Die Änderung für den Schlüssel `dashboard`, wenn ein Bereich ein- oder
 * ausgeschaltet wird. Ein gewöhnlicher Bereich ändert nur `ausgeblendet`;
 * einer mit `standardAus` (#650) wird über `eingeblendet` geschaltet und
 * zugleich aus `ausgeblendet` genommen, falls er dort steht. Fremde ids
 * bleiben in beiden Listen stehen.
 */
export function schalteBereich(zustand, id, an, register = BEREICHE) {
  const aus = liste(zustand.ausgeblendet).filter((x) => x !== id);
  const bereich = register.find((b) => b.id === id);
  if (!bereich || !bereich.standardAus) return { ausgeblendet: an ? aus : [...aus, id] };
  const ein = liste(zustand.eingeblendet).filter((x) => x !== id);
  const teil = { eingeblendet: an ? [...ein, id] : ein };
  if (aus.length !== liste(zustand.ausgeblendet).length) teil.ausgeblendet = aus;
  return teil;
}

/** Die Bereiche, die die Rolle zeigt, in Reihenfolge, ob ausgeblendet oder nicht. */
export function erlaubteBereiche(zustand, register = BEREICHE) {
  return ordneBereiche(register, zustand.reihenfolge).filter((b) => !b.bedingung || b.bedingung(zustand));
}

/**
 * Die sichtbaren Bereiche in Reihenfolge, je mit Platz ('band' oder 'raster').
 * @param {{ siehtPool: boolean, ausgeblendet?: string[], eingeblendet?: string[], reihenfolge?: string[], showTodayCalendar: boolean }} zustand
 * @param {Array} register  fuer Tests; Standard ist BEREICHE
 */
export function sichtbareBereiche(zustand, register = BEREICHE) {
  return erlaubteBereiche(zustand, register)
    .filter((b) => !istAusgeblendet(b, zustand))
    .map((b) => ({ ...b, platz: b.platz ? b.platz(zustand) : 'raster' }));
}

/**
 * Neue Reihenfolge, wenn ein Bereich in der sichtbaren Folge eins nach oben
 * (-1) oder unten (+1) rueckt. Gespeichert wird die Folge des ganzen
 * Registers; Bereiche, die die Rolle nicht zeigt, behalten ihren Platz, und
 * ids, die das Register nicht (mehr) kennt, bleiben hinten stehen.
 */
export function verschiebeBereich(zustand, id, richtung, register = BEREICHE) {
  const alle = ordneBereiche(register, zustand.reihenfolge).map((b) => b.id);
  const erlaubt = erlaubteBereiche(zustand, register).map((b) => b.id);
  const i = erlaubt.indexOf(id);
  const j = i + richtung;
  if (i === -1 || j < 0 || j >= erlaubt.length) return null;
  const getauscht = erlaubt.slice();
  [getauscht[i], getauscht[j]] = [getauscht[j], getauscht[i]];
  let k = 0;
  const neu = alle.map((x) => (erlaubt.includes(x) ? getauscht[k++] : x));
  const fremde = liste(zustand.reihenfolge).filter((x) => !alle.includes(x));
  return [...neu, ...fremde];
}
